import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { compose, graphql, withApollo } from 'react-apollo';
import { connectLean } from 'lean-redux';
import { v4 as uuidv4 } from 'uuid';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { LoadingSpinner } from 'legacy-packages/ft-cb';
import VideoModal from 'legacy/src/components/VideoModal';
import {
  getVideoReference,
  storeDailyVideoProgress,
} from 'legacy/packages/wizards/src/queries';
import {
  getAggregatedVideoStats,
  getAdminCoordAggregatedVideoStats,
  getVideoPlayTimePercentages,
} from 'legacy/src/queries';
import WatchedPopover from '../WatchedPopover';
import VideoProgressCallbacks from './components/VideoProgressCallbacks';
import CarouselWrapper from './components/CarouselWrapper';
import {
  calculateWatchPercentage,
  getStatus,
} from 'chameleon/legacy/src/services/video/utils.js';
import {
  VIDEO_PROGRESS,
  VIEWED_THRESHOLD,
} from 'chameleon/legacy/src/services/video/constants.js';
import createProgressTrackingKey from 'chameleon/legacy/src/services/video/utils/createProgressTrackingKey';
import useFetchProgress from './hooks/useFetchProgress';
import NoClassAlert from 'legacy/src/utility/NoClassAlert';
import ScreenModal from 'chameleon/ui-stack/ui/modals/screen-modal';
import ResourceAssign from 'chameleon-src/components/assign/ResourceAssign';
import log from 'chameleon/ui-stack/utilities/log';
import './styles.scss';
import { connect } from 'react-redux';

const SUB_TITLES = {
  AlwaysShow: 'This video is visible to all students on their homepage.',
  NeverShow: 'This video is never shown to students',
  Shareable:
    'This video is visible to students on their homepage if it has been shared to them.',
  ShowAndShare: 'This video is visible to all students on their homepage.',
};

const VideoResourcePreview = (props) => {
  const {
    videoResource,
    userClasses,
    videoId,
    userData,
    router,
    subject,
    client,
    data,
    updateSharedResourceWatchStatus,
    setActiveUnit,
    videoModalQuery,
    allAvailableVideoResources,
    onVideoSelection,
    studentClasses,
    useFilterableResources,
    filteredResources,
    loadingFilteredResources,
    withCarousel,
    filteredResourcesLabel,
    filteredClasses,
    showZeroWatchedFlag,
    apdCarouselData,
    updateSingleVideoAPDCarouselData,
    watchedVideosInSession,
    setWatchedVideosInSession,
    isImpersonating,
    setAPDCarouselData,
    persistAssignedSections,
    isSPP,
    onAssign,
    threeplayPluginFF,
  } = props;
  const { location } = router;
  const { aggregatedVideoStatus } = data || {};
  const {
    isStudent,
    isTeacher,
    me: { initId: userId, importId: cbPersonid },
    isAdmin,
    isCoordinator,
  } = userData;
  const currentClasses = (userClasses || []).filter(
    (item) =>
      item.educationPeriod.toString() === userData.chosenEducationPeriodCode,
  );
  const {
    resource,
    unit: { unitId },
  } = videoResource;
  const {
    resourceId,
    displayName: resourceName,
    videoId: apcVideoId,
    url,
    visibilityToStudents,
  } = resource;
  const highlightElementRef = useRef(url);
  const connectLeanResourceScope = [
    'units',
    `unit-${unitId}`,
    'resources',
    `resource-${resourceId}-subject-${subject.id}`,
  ];
  const isAdminOrCoordinator = isAdmin || isCoordinator;
  const subjectId = parseInt(subject.id, 10);
  let [loadingPreviousProgress, setloadingPreviousProgress] = useState(true);
  const [previousProgress, setPreviousProgress] = useState([]);
  const [videoPosition, setVideoPosition] = useState(null);
  const [completionProgress, setCompletionProgress] = useState([]);
  // 3play video id
  const [videoReference, setVideoReference] = useState('');

  const checkCompletionProgress = (progress) => {
    const watchedPercentage = calculateWatchPercentage(progress);
    const watchStatus = getStatus(watchedPercentage);

    if (!completionProgress.length && watchStatus === 'COMPLETE') {
      setCompletionProgress(progress);
    }
  };

  useFetchProgress({
    client,
    videoId: apcVideoId,
    userId: parseInt(userId, 10),
    subjectId,
    isStudent,
    isTeacher,
    setloadingPreviousProgress,
    setPreviousProgress,
    setVideoPosition,
    loadingPreviousProgress,
    completionProgress: {
      set: setCompletionProgress,
      check: checkCompletionProgress,
    },
  });

  useEffect(() => {
    if (!threeplayPluginFF) {
      return;
    }
    client
      .query({
        query: getVideoReference,
        fetchPolicy: 'network-only',
        variables: {
          providerVideoId: videoId,
        },
      })
      .then(({ data }) => {
        setVideoReference(data.videoReference);
      });
  }, [videoId]);

  const lastProgressRef = useRef(previousProgress);
  const currentWatchStatus = useRef(
    `${
      isEmpty(lastProgressRef.current)
        ? VIDEO_PROGRESS.NOT_STARTED
        : VIDEO_PROGRESS.STARTED
    }`,
  );
  let refsUpdated = useRef(false);

  useEffect(() => {
    if (!loadingPreviousProgress) {
      refsUpdated.current = true;
      lastProgressRef.current = previousProgress;
      currentWatchStatus.current = `${
        isEmpty(previousProgress)
          ? VIDEO_PROGRESS.NOT_STARTED
          : VIDEO_PROGRESS.STARTED
      }`;
    }
  }, [previousProgress, loadingPreviousProgress]);

  const onStoreDailyProgress = (
    { progress, latestVideoPosition },
    isOnClose = false,
    updateResourceWatchedStatus,
  ) => {
    /**
     * before refs are updated in the useEffect we could end up resetting the
     * progress of a user
     *  */
    if (!refsUpdated.current) {
      return;
    }

    const newProgress = completionProgress.length
      ? completionProgress
      : progress;

    const watchedPercentage = calculateWatchPercentage(newProgress);
    const watchStatus = getStatus(watchedPercentage);
    const playTimePercentage = latestVideoPosition;
    /**
     * Avoid storing progress for video
     * - that is NOTSTARTED when the user click's on the X button.
     * - when newProgress is an empty array, this should never happen
     */
    if (
      (currentWatchStatus.current === VIDEO_PROGRESS.NOT_STARTED &&
        isOnClose) ||
      (newProgress || []).length === 0
    ) {
      return;
    }

    // set watched status based on the VIEWED_THRESHOLD
    if (watchedPercentage >= VIEWED_THRESHOLD) {
      updateResourceWatchedStatus && updateResourceWatchedStatus(true, true);
      setWatchedVideosInSession &&
        setWatchedVideosInSession([...watchedVideosInSession, resourceId]);
    } else if (watchedPercentage > 0) {
      updateResourceWatchedStatus && updateResourceWatchedStatus(false, true);
    }

    const payload = {
      userId: parseInt(userId, 10),
      videoId: parseInt(apcVideoId, 10),
      progress: newProgress,
      status: watchStatus,
      cbPersonid: cbPersonid || userId,
      watchedPercentage,
      playTimePercentage,
    };

    // update progress bar as video plays
    updateSingleVideoAPDCarouselData({
      subject: subjectId,
      videoId,
      playTimePercentage,
    });

    // keep track of the newProgress value
    lastProgressRef.current = newProgress;
    currentWatchStatus.current = watchStatus;

    // don't mutate if teacher is impersonating a student
    if (!isImpersonating) {
      client
        .mutate({
          mutation: storeDailyVideoProgress,
          variables: payload,
        })
        .catch((err) => {
          log('error', err);
        });
    }
  };

  const hasNoClasses = !currentClasses.length;
  const shareProps = {
    ...props,
    userClasses: currentClasses,
    items: [resource],
    hasNoClasses,
    onSubmit: (sectionIds) => {
      onAssign && onAssign();
      persistAssignedSections(
        sectionIds.map((value) => Number.parseInt(value, 10)),
      );
    },
  };

  if (hasNoClasses) {
    shareProps.noClassAlert = (
      <NoClassAlert
        title=""
        content="Create a class section in order to assign this video."
        description="Assign this video to emphasize it on students' AP Classrooom homepage with a To-Do card."
        withPadding={false}
      />
    );
    shareProps.modalTitle = 'Assign Video';
  }

  const renderShareWithAssignButton = (props) => (
    <ResourceAssign {...props} isAdminOrCoordinator={isAdminOrCoordinator} />
  );

  const renderWatchedPopover = () => {
    const { stats, totalStudents, watchedStudents } =
      aggregatedVideoStatus || {};
    /**
     * show popover if
     * - is admin/coord and they will see the placeholder text
     * - is teacher/admin/coord and they have >= 0% percent watched
     */
    let shouldShowPopover = isTeacher || isAdminOrCoordinator;
    if (!showZeroWatchedFlag) {
      const watchedPercentage =
        totalStudents > 0 && watchedStudents > 0
          ? (watchedStudents / totalStudents) * 100
          : 0;
      const roundedWatched = Math.round(watchedPercentage);
      shouldShowPopover = !!roundedWatched;
    }

    return (
      shouldShowPopover && (
        <WatchedPopover
          subject={subject}
          router={router}
          scope={connectLeanResourceScope}
          stats={stats || []}
          totalStudents={totalStudents}
          watchedStudents={watchedStudents}
          isAdminOrCoordinator={isAdminOrCoordinator}
        />
      )
    );
  };
  const showVideoModal =
    !loadingPreviousProgress &&
    videoPosition != null &&
    videoPosition >= 0 &&
    (!threeplayPluginFF || (threeplayPluginFF && videoReference !== ''));
  const progressTrackingKey = createProgressTrackingKey(
    {
      userData,
    },
    isImpersonating,
  );

  const handleModalClose = () => {
    /**
     * It's only after refs are updated that we have
     * an accurate watch status to use
     */
    if (refsUpdated.current) {
      updateSharedResourceWatchStatus(resourceId, currentWatchStatus.current);
    }

    setActiveUnit &&
      setActiveUnit({
        unitId,
        subjectId: subject.id,
        userId,
      });

    router.push({
      pathname: location.pathname,
      query: {
        ...videoModalQuery,
        highlightElement: highlightElementRef.current,
      },
      focusedElementVideo: location.state && location.state.focusedElementVideo,
    });
  };

  const viewVideo = (newVideoResource) => {
    if (newVideoResource) {
      const { url } = newVideoResource;
      if (videoId !== url) {
        router.push({
          pathname: router.location.pathname,
          query: {
            ...videoModalQuery,
            apd: url,
          },
        });
        onVideoSelection && onVideoSelection(newVideoResource, unitId);
      }
    }
  };

  const onStoreProgressThenCloseModal = (event) => {
    event.preventDefault();
    handleModalClose && handleModalClose();
  };

  const player = (showVideoModal && (
    <VideoProgressCallbacks
      scope={connectLeanResourceScope}
      userData={userData}
      onStoreDailyProgress={onStoreDailyProgress}
      render={({ eventCallbacks }) => (
        <VideoModal
          progressTrackingKey={progressTrackingKey}
          router={router}
          videoId={videoId}
          videoReference={videoReference}
          userData={userData}
          videoPosition={videoPosition}
          userId={userId}
          eventCallbacks={eventCallbacks}
          unitId={unitId}
          apcVideoId={apcVideoId}
          progressVector={completionProgress.length ? [] : previousProgress}
          isImpersonating={isImpersonating}
          apdCarouselData={apdCarouselData}
          setAPDCarouselData={setAPDCarouselData}
          isSPP={isSPP}
          threeplayPluginFF={threeplayPluginFF}
        />
      )}
    />
  )) || <LoadingSpinner containerClassName="flex" />;

  if (isSPP) {
    return player;
  }

  const topBar = (
    <Fragment>
      {!isStudent && renderWatchedPopover()}
      {!isStudent && renderShareWithAssignButton(shareProps)}
    </Fragment>
  );

  const subTitle = !isStudent ? (
    <span className="text-14 text-gray-600">
      {SUB_TITLES[visibilityToStudents]}
    </span>
  ) : null;

  return (
    <ScreenModal
      contentWithSpacing={false}
      dialogClassName="VideoDialog"
      mainContainerId={uuidv4()}
      title={resourceName}
      subTitle={subTitle}
      topBar={topBar}
      onClose={(event) => {
        onStoreProgressThenCloseModal(event);
      }}
    >
      {player}
      {!isSPP && withCarousel && (
        <CarouselWrapper
          useFilterableResources={useFilterableResources}
          resourceId={resourceId}
          allAvailableVideoResources={allAvailableVideoResources}
          hasFilteredResources={Boolean(filteredResources.length)}
          filteredResources={filteredResources}
          loadingFilteredResources={loadingFilteredResources}
          viewVideo={viewVideo}
          studentClasses={studentClasses}
          isStudent={isStudent}
          isTeacher={isTeacher}
          subject={subject}
          router={router}
          apdCarouselData={apdCarouselData}
          filteredResourcesLabel={filteredResourcesLabel}
          filteredClasses={filteredClasses}
        />
      )}
    </ScreenModal>
  );
};

VideoResourcePreview.propTypes = {
  videoId: PropTypes.string.isRequired,
  userData: PropTypes.object.isRequired,
  router: PropTypes.object.isRequired,
  subject: PropTypes.object.isRequired,
  userClasses: PropTypes.array.isRequired,
  isSPP: PropTypes.bool,
  /**
   * @todo use it to render loadingIndicator while units are loading and video preview is opened
   * */
  loadingData: PropTypes.bool,
  videoResource: PropTypes.object.isRequired,
  updateSharedResourceWatchStatus: PropTypes.func,
  videoModalQuery: PropTypes.object,
  allAvailableVideoResources: PropTypes.array,
  onVideoSelection: PropTypes.func,
  useFilterableResources: PropTypes.bool,
  /**
   * Used to toggle with all resources
   * */
  filteredResources: PropTypes.array,
  loadingFilteredResources: PropTypes.bool,
  setAPDCarouselData: PropTypes.func,
  updateSingleVideoAPDCarouselData: PropTypes.func,
  withCarousel: PropTypes.bool,
  filteredResourcesLabel: PropTypes.string,
  showZeroWatchedFlag: PropTypes.bool,
  filteredClasses: PropTypes.array,
  setWatchedVideosInSession: PropTypes.func,
  watchedVideosInSession: PropTypes.array,
  isImpersonating: PropTypes.bool.isRequired,
  threeplayPluginFF: PropTypes.bool,
};

VideoResourcePreview.defaultProps = {
  loadingData: false,
  updateSharedResourceWatchStatus: () => {},
  videoModalQuery: {},
  allAvailableVideoResources: [],
  onVideoSelection: () => {},
  useFilterableResources: false,
  filteredResources: [],
  loadingFilteredResources: false,
  setAPDCarouselData: () => {},
  updateSingleVideoAPDCarouselData: () => {},
  withCarousel: false,
  showZeroWatchedFlag: false,
  filteredClasses: [],
  setWatchedVideosInSession: () => {},
  watchedVideosInSession: [],
  filteredResourcesLabel: '',
  isSPP: false,
  threeplayPluginFF: false,
};

const mapStateToProps = (state) => {
  const { featureFlag } = state;

  return {
    threeplayPluginFF: featureFlag && featureFlag.threeplay_plugin,
  };
};

export default compose(
  withApollo,
  // get teacher/ student play time percentages for carousel video progress bar
  graphql(getVideoPlayTimePercentages, {
    options: (props) => {
      const {
        allAvailableVideoResources,
        router: {
          params: { subject },
        },
        userData: { isTeacher, isStudent },
        isSPP,
      } = props;
      const providerVideoIds = Array.from(
        allAvailableVideoResources || [],
        ({ url }) => url,
      );
      return {
        skip:
          !!isSPP || !((isTeacher || isStudent) && providerVideoIds.length > 0),
        fetchPolicy: 'network-only',
        variables: { subjectId: subject, providerVideoIds },
      };
    },
    props: ({
      data: {
        loading,
        getVideoPlayTimePercentages: getVideoPlayTimePercentagesResult,
      },
      ownProps: { setAPDCarouselData, subject, apdCarouselData },
    }) => {
      const dataByVideoParsed =
        (getVideoPlayTimePercentagesResult &&
          JSON.parse(getVideoPlayTimePercentagesResult)) ||
        {};
      const videoData = get(
        dataByVideoParsed,
        ['dailyVideoProgress', 'videoPlayTimePercentages'],
        [],
      ).reduce(
        (accumulator, currentValue) => ({
          ...accumulator,
          [currentValue.providerVideoId]: { ...currentValue },
        }),
        {},
      );
      !loading &&
        (!apdCarouselData || !apdCarouselData[subject.id]) &&
        setAPDCarouselData({ subject: subject.id, videoData });
    },
  }),
  // teacher statistics call
  graphql(getAggregatedVideoStats, {
    options: (props) => {
      const {
        videoResource: {
          resource: { resourceId, videoId },
        },
        router: {
          params: { subject },
        },
        userData: { isTeacher },
        isSPP,
      } = props;
      return {
        skip: !!isSPP || !isTeacher,
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        variables: {
          resourceIds: [resourceId],
          subjectId: subject,
          videoIds: [videoId],
        },
      };
    },
  }),
  // admin/ coord call
  graphql(getAdminCoordAggregatedVideoStats, {
    options: (props) => {
      const {
        videoResource: {
          resource: { resourceId, videoId },
        },
        subject: { id: subjectId },
        userData: { isAdmin, isCoordinator },
        isSPP,
      } = props;
      return {
        skip: !(isAdmin || isCoordinator) || !!isSPP,
        fetchPolicy: 'network-only',
        variables: {
          resourceIds: [resourceId],
          subjectId,
          videoIds: [videoId],
        },
      };
    },
  }),
  connect(mapStateToProps),
  connectLean({
    scope: ({
      videoResource: {
        unit: { unitId },
        resource: { resourceId },
      },
      subject,
    }) => [
      'units',
      `unit-${unitId}`,
      'resources',
      `resource-${resourceId}-subject-${subject.id}`,
    ],
    mapState(state) {
      const { selectedItems, savedClasses } = state;

      return {
        selectedItems,
        savedClasses,
      };
    },
    persistAssignedSections(items) {
      this.setState(({ savedClasses = [] }) => {
        return {
          savedClasses: [...items, ...savedClasses],
          selectedItems: items,
        };
      });
    },
  }),
)(VideoResourcePreview);
